// Reporter de errores de CLIENTE (browser). Lo usan los error boundaries
// (error.tsx / global-error.tsx) y los handlers globales de
// instrumentation-client.ts.
//
// 100% client-safe: NO importa nada server-only. Envía por fetch a la API
// route /api/observability/client-error (que deriva rol/user y persiste).
// Importar un server action acá arrastraba postgres/db al bundle del browser
// (la directiva "use server" no corta el grafo al pasar por un módulo plano
// hacia instrumentation-client), así que el transporte es un fetch plano.
//
// Tres reglas para que el tracking sea útil y no ruidoso:
//   1. Dedup server<->client: si el error tiene `digest`, ya lo capturó
//      instrumentation.onRequestError server-side (con mejor stack), así que
//      NO lo duplicamos desde el cliente — salvo `force` (global-error fatal).
//   2. Throttle/dedup local: colapsa el mismo error repetido en una ventana
//      corta (un loop de render puede tirarlo decenas de veces).
//   3. Best-effort: jamás lanza; reportar un error nunca puede romper la app.

type Severity = "info" | "warning" | "error" | "fatal";
type ErrorSource = "client" | "server" | "edge";

type ReportInput = {
  error: unknown;
  context: string;
  severity?: Severity;
  source?: ErrorSource;
  // Reporta aunque tenga digest (casos fatales: global-error).
  force?: boolean;
};

const ENDPOINT = "/api/observability/client-error";
const DEDUP_WINDOW_MS = 10_000;
const MAX_SIGNATURES = 100;
const seen = new Map<string, number>();

function alreadyReported(signature: string): boolean {
  const now = Date.now();
  if (seen.size > MAX_SIGNATURES) {
    for (const [k, t] of seen) {
      if (now - t > DEDUP_WINDOW_MS) seen.delete(k);
    }
  }
  const last = seen.get(signature);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return true;
  seen.set(signature, now);
  return false;
}

function toMessageAndStack(error: unknown): {
  message: string;
  stack?: string;
  digest?: string;
} {
  if (error instanceof Error) {
    const digest = (error as { digest?: unknown }).digest;
    return {
      message: error.message || error.name || "Error",
      stack: error.stack,
      digest: typeof digest === "string" ? digest : undefined,
    };
  }
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

export function reportClientError(input: ReportInput): void {
  try {
    const { message, stack, digest } = toMessageAndStack(input.error);

    // Regla 1: errores originados en el server → los captura onRequestError.
    if (digest && !input.force) return;

    // Regla 2: dedup local por firma (mensaje + primer frame).
    const signature = `${input.context}|${message}|${(stack ?? "").slice(0, 120)}`;
    if (alreadyReported(signature)) return;

    const payload = {
      message: message.slice(0, 8000),
      stack: stack?.slice(0, 20000),
      context: input.context,
      path:
        typeof window !== "undefined" ? window.location.pathname : undefined,
      digest,
      severity: input.severity,
      source: input.source ?? "client",
    };

    // keepalive: que el reporte sobreviva a una navegación/cierre de pestaña.
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // si el reporte no llega, no rompemos nada
    });
  } catch {
    // Reportar nunca puede romper la app.
  }
}
